import { useEffect } from 'react';
import { FormikValues, useFormik } from 'formik';

import {
  AdminKeyToParse,
  adminUpdateShiftContract,
  adminWorkshiftDetails,
  AllowanceEnums,
  AllowancesNames,
  AllowanceTypesToHandle,
  ContractTypes,
  editAdminWorkshift,
  setBookedContractDates,
  TravelAllowanceEnums,
} from '@pharmaplan/common';
import { handleWorkshiftToggles } from '@pharmaplan/common/helpers/utils';
import { useAppSelector } from '../../useAppSelector';
import { appendTimeToDate } from '../../../helpers/workshift/functions';
import { MomentTimeFormats } from '../../../helpers/Constants';
import { useAppDispatch } from '../../useAppDispatch';
import useSelfService from '../../useSelfService';
import { toJSDate } from '../../../components/Dashboard/MainCalendar/helpers';
import {
  decimalConverterProxy,
  handlerForZeroes,
  parseFloatProxy,
} from '../../../helpers/proxyHandlers';
import { AdminEditWorkshiftValidator } from '../../../helpers/commonValidators';
import useSyncWorkshiftValues from '../../workshift/useSyncWorkshiftValues';
import { rateValidation } from '../../../helpers/Functions';
import useUpdateContract from '../../LongTermContract/useUpdateContract';
import useGetChangedContract from '../../LongTermContract/useGetChangedContract';

const {
  accommodationAllowanceType,
  travelAllowanceType,
  travelAllowanceRatePerKm: travelAllowanceRatePerKmEnum,
  travelAllowanceAmount: travelAllowanceAmountEnum,
  mealAllowance: mealAllowanceEnum,
  accommodationAllowance: accommodationAllowanceEnum,
  emergencyFees: emergencyFeesEnum,
  paidHourlyRate,
  otherAllowance: otherAllowanceEnum,
} = AllowancesNames;
const { fullDate } = MomentTimeFormats;
const { meal, travel, accommodation, emergency, others } = AllowanceEnums;

const useAdminEditWorkshift = (workshiftId: string) => {
  const dispatch = useAppDispatch();
  const { isSelfService } = useSelfService();

  const { setAttributes, formatDisplayDate } = useUpdateContract();
  const { getChangedItems, comparator } = useGetChangedContract();

  const currentWorkshift = useAppSelector(adminWorkshiftDetails);
  const { workshiftParser } = useSelfService();

  const {
    startDate,
    endDate,
    note,
    emergencyFees: workshiftEmergencyFees,
    mealAllowance: workshiftMealAllowance,
    accommodationAllowance: workshiftAccommodationAllowance,
    pharmacyHourlyRate,
    pharmacistHourlyRate: workshiftPharmacistRate,
    travelAllowanceAmount,
    travelAllowanceRatePerKm,
    accommodationAllowanceType: accommodationType,
    travelAllowanceTypes,
    otherAllowance,
    replacementType,
    longTermShiftContractId,
    longTermShiftContract,
  } = currentWorkshift ?? {};

  const {
    startDate: longContractStart,
    endDate: longContractEnd,
    weekDayPaidHourlyRate,
    weekendPaidHourlyRate,
    allowanceNotes,
  } = longTermShiftContract ?? {};

  const handleSubmit = (values: FormikValues) => {
    const {
      startTime,
      endTime,
      paidHourlyRate: formikPaidHourlyRate,
      pharmacyHourlyRate: formikPharmacyHourlyRate,
      weekDayPaidHourlyRate: weekRate,
      weekendPaidHourlyRate: weekendRate,
    } = values ?? {};

    if (values?.contractType === ContractTypes.longTerm) {
      const { startDate: compareStart, endDate: compareEnd } = comparator();

      const dateParams = {
        startDate: compareStart,
        endDate: compareEnd,
      };

      dispatch(
        adminUpdateShiftContract({
          ...dateParams,
          allowanceNotes: values?.allowanceNotes,
          isLongTermContract: values?.contractType === ContractTypes.longTerm,
          shiftContractAttributes: getChangedItems(),
          typeReplacement: values?.replacementType,
          weekendPaidHourlyRate: parseFloat(weekRate),
          weekDayPaidHourlyRate: parseFloat(weekendRate),
          workshiftId,
          longTermContractId: longTermShiftContractId ?? '',
          note: values.notes,
        }),
      );
      return;
    }

    const val = {
      travelAllowanceRatePerKm: handleWorkshiftToggles(
        values[travelAllowanceType]?.includes(TravelAllowanceEnums.valueKm),
        values[travelAllowanceRatePerKmEnum],
        null,
      ),
      travelAllowanceAmount: handleWorkshiftToggles(
        values[travelAllowanceType].includes(TravelAllowanceEnums.travelAmount),
        values[travelAllowanceAmountEnum],
        null,
      ),
      mealAllowance: handleWorkshiftToggles(
        values[meal],
        values[mealAllowanceEnum],
        null,
      ),
      accommodationAllowance: handleWorkshiftToggles(
        values[accommodationAllowanceType]
          === AllowanceTypesToHandle.paidValuePerNight,
        values[accommodationAllowanceEnum],
        null,
      ),
      emergencyFees: handleWorkshiftToggles(
        values[emergency],
        values[emergencyFeesEnum],
        null,
      ),
      otherAllowance: handleWorkshiftToggles(
        values[others],
        values[otherAllowanceEnum],
        null,
      ),
      [paidHourlyRate]: formikPaidHourlyRate,
    };

    const start = appendTimeToDate(startDate, startTime).toFormat(fullDate);
    const end = appendTimeToDate(endDate, endTime).toFormat(fullDate);

    const handledForDecimals = new Proxy(val, parseFloatProxy);

    const isPharmacyGreaterPharmacist = rateValidation({
      pharmacistRate: formikPaidHourlyRate,
      pharmacyRate: formikPharmacyHourlyRate,
      isSelfService,
      dispatch,
    });

    if (isPharmacyGreaterPharmacist) {
      dispatch(
        editAdminWorkshift({
          ...workshiftParser(values, AdminKeyToParse),
          ...handledForDecimals,
          workshiftId,
          startDate: start,
          endDate: end,
          travelAllowanceTypes: values[travelAllowanceType],
          accommodationAllowanceType: handleWorkshiftToggles(
            values[accommodation],
            values[accommodationAllowanceType],
            null,
          ),
          note: values.notes,
        }),
      );
    }
  };

  const valuesToParse = {
    travelAllowanceRatePerKm,
    mealAllowance: workshiftMealAllowance,
    emergencyFees: workshiftEmergencyFees,
    accommodationAllowance: workshiftAccommodationAllowance,
    travelAllowanceAmount,
    paidHourlyRate: workshiftPharmacistRate,
    pharmacyHourlyRate,
    otherAllowance,
    weekDayPaidHourlyRate,
    weekendPaidHourlyRate,
  };

  const handledForZero = new Proxy(valuesToParse, handlerForZeroes);
  const handledForDecimals = new Proxy(handledForZero, decimalConverterProxy);

  const contractType = longTermShiftContractId
    ? ContractTypes.longTerm
    : ContractTypes.shortTerm;

  let dates: Record<string, string | undefined> = {
    endDate,
  };

  if (longTermShiftContractId) {
    dates = {
      endDate: longContractEnd,
      startDate: longContractStart,
    };
  }

  const formik = useFormik({
    initialValues: {
      ...dates,
      ...handledForDecimals,
      startTime: toJSDate(startDate),
      displayDate: formatDisplayDate(longContractStart, longContractEnd),
      endTime: toJSDate(endDate),
      contractType,
      notes: note,
      replacementType,
      allowanceNotes,
      [accommodationAllowanceType]: `${accommodationType}`,
      [AllowancesNames.travelAllowanceType]:
        currentWorkshift?.travelAllowanceTypes?.filter(Boolean),
    },
    validationSchema: AdminEditWorkshiftValidator(),
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  useSyncWorkshiftValues(formik);
  useEffect(() => {
    formik.setFieldValue(meal, !!workshiftMealAllowance);
    formik.setFieldValue(travel, !!travelAllowanceTypes?.length);
    formik.setFieldValue(accommodation, !!accommodationType);
    formik.setFieldValue(emergency, !!workshiftEmergencyFees);
    formik.setFieldValue(others, !!otherAllowance);
    setAttributes(longContractStart, longContractEnd);
    dispatch(
      setBookedContractDates(
        currentWorkshift.bookedDates?.map?.(
          (item) =>
            item?.split?.('T')?.[0],
        ) ?? [],
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkshift]);

  return { formik };
};

export default useAdminEditWorkshift;
